import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MessageMedium } from 'constants/messageMediums';
import { Workflow, Node } from 'modules/workflows/types';
import { WorkflowType } from 'modules/workflows/enums';
import styled from 'styled-components';
import { colors, spacing, subtitle2 } from 'ui/theme';
import { MultipleUsers24 } from 'ui/icons';
import {
  getHasMetricsDateChanged,
  fetchJourneyVisitorMetricsRequest,
  getFlexMetricsDateRange,
  openTakeover,
} from 'modules/workflows/ducks';
import { ContactCounts } from './ContactCounts';
import { FlexMetricsRangeSelect } from '../FlexMetricsRangeSelect';
import { JourneyPanelTitle, JourneyPanelBody, ContactsContainer } from './styled';
import { ContactCountsJVC } from './ContactCounts/ConactCountsJVC';
import { BreakdownType } from '../takeovers/JourneyVisitorBreakdownTakeover/JourneyVisitorBreakdown.constant';
import { TakeoverType } from '../takeovers/takeovers.constants';

export const JOURNEY_ANALYTICS_CARD_TEST_IDS = {
  CONTAINER: 'journey-analytics-card-container',
};

interface JourneyAnalyticsCardProps {
  /** The current workflow */
  workflow: Workflow;
  /** Should the view insights links be shown? */
  showViewInsightsLinks?: boolean;
  /** The mediums in the current journey */
  mediumsInWorkflow: MessageMedium[];
  /** Node to render on the selected node card */
  node?: Node;
}

const ContactsTileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
  flex: 1 0 0;
`;

const ContactsTitle = styled.div`
  color: ${colors.textPrimary};
  ${subtitle2}
`;
const JourneyAnalyticsCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const JourneyAnalyticsCardContainer = styled.div`
  padding-bottom: ${spacing.base};
`;

export const JourneyAnalyticsCard = ({ workflow }: JourneyAnalyticsCardProps) => {
  const hasMetricsDateChanged = useSelector(getHasMetricsDateChanged);

  const { id: workflowId, workflowType, visitorTrackingEnabled } = workflow || {};
  const dispatch = useDispatch();
  const openJourneyVisitorBreakdown = (breakdownType: BreakdownType, numUserTotal?: number) => {
    return () =>
      dispatch(openTakeover(TakeoverType.JourneyVisitorBreakdown, { breakdownType, numUserTotal }));
  };
  const dateRange = useSelector(getFlexMetricsDateRange);

  useEffect(() => {
    const { startDateString, endDateString } = dateRange || {};
    if (workflowType === WorkflowType.PUBLISHED && !!startDateString && !!endDateString) {
      dispatch(fetchJourneyVisitorMetricsRequest(workflowId, { startDateString, endDateString }));
    }
  }, [workflowId, workflowType, dateRange, visitorTrackingEnabled]);

  return (
    <JourneyAnalyticsCardWrapper>
      <JourneyAnalyticsCardContainer data-test={JOURNEY_ANALYTICS_CARD_TEST_IDS.CONTAINER}>
        <ContactsContainer>
          {workflow?.id && (
            <>
              <JourneyPanelTitle>
                <FlexMetricsRangeSelect />
                <ContactsTileContainer>
                  <MultipleUsers24 />
                  <ContactsTitle>Total user traffic</ContactsTitle>
                </ContactsTileContainer>
              </JourneyPanelTitle>
              <JourneyPanelBody>
                {hasMetricsDateChanged ? (
                  <ContactCountsJVC openJourneyVisitorBreakdown={openJourneyVisitorBreakdown} />
                ) : (
                  <ContactCounts
                    workflowId={workflow.id}
                    workflowType={workflow.workflowType as WorkflowType}
                    openJourneyVisitorBreakdown={openJourneyVisitorBreakdown}
                  />
                )}
              </JourneyPanelBody>
            </>
          )}
        </ContactsContainer>
      </JourneyAnalyticsCardContainer>
    </JourneyAnalyticsCardWrapper>
  );
};
